import React, { useState, useRef } from 'react';
import { Workplace } from '../../types';
import Swal from 'sweetalert2';
import './UploadDocumentModal.css';

export interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  workplace: Workplace | null;
  documentList: Array<{
    id: number;
    code: string;
    title: string;
  }>;
  preSelectedDocCode?: string;
  onUploadSuccess: (docCode: string, uploadedFiles: File[], documentDate?: string) => void;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  workplace,
  documentList,
  preSelectedDocCode,
  onUploadSuccess
}) => {
  const [selectedDocCode, setSelectedDocCode] = useState<string>(preSelectedDocCode || (documentList[0]?.code || ''));
  const [documentDate, setDocumentDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Güncelleme durumunda varsayılan seçimi senkronize et
  React.useEffect(() => {
    if (preSelectedDocCode) {
      setSelectedDocCode(preSelectedDocCode);
    } else if (documentList.length > 0 && !selectedDocCode) {
      setSelectedDocCode(documentList[0].code);
    }
  }, [preSelectedDocCode, documentList, selectedDocCode]);

  // Modal her açıldığında belge tarihini bugüne sıfırla
  React.useEffect(() => {
    if (isOpen) {
      setDocumentDate(new Date().toISOString().split('T')[0]);
    }
  }, [isOpen]);

  if (!isOpen || !workplace) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      setSelectedFiles(prev => [...prev, ...droppedFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDocCode) {
      Swal.fire({
        icon: 'warning',
        title: 'Belge Türü Seçilmedi',
        text: 'Lütfen yüklenecek belgenin türünü seçin.',
        confirmButtonColor: '#db2777'
      });
      return;
    }

    if (selectedFiles.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Evrak Seçilmedi',
        text: 'Lütfen bilgisayarınızdan en az bir evrak/dosya seçin.',
        confirmButtonColor: '#db2777'
      });
      return;
    }

    const docInfo = documentList.find(d => d.code === selectedDocCode);
    const docTitle = docInfo ? docInfo.title : 'Belge';

    // Durumu HAZIR yap ve üst bileşene belge tarihi ile birlikte bildir
    onUploadSuccess(selectedDocCode, selectedFiles, documentDate);

    Swal.fire({
      icon: 'success',
      title: 'Evraklar Yüklendi!',
      text: `"${docTitle}" için ${selectedFiles.length} adet dosya başarıyla eklendi ve durumu HAZIR olarak güncellendi.`,
      confirmButtonColor: '#16a34a',
      timer: 2300,
      timerProgressBar: true
    });

    // Sıfırla ve kapat
    setSelectedFiles([]);
    onClose();
  };

  return (
    <div className="upload-doc-overlay" onClick={onClose}>
      <div className="upload-doc-modal" onClick={e => e.stopPropagation()}>
        {/* Üst Başlık — Pembe Tema */}
        <div className="upload-doc-header">
          <div className="upload-header-left">
            <span className="upload-header-badge">📑 İSG DÖKÜMANTASYON SİSTEMİ</span>
            <h2 className="upload-header-title">Yeni Belge &amp; Evrak Yükle</h2>
          </div>
          <button
            type="button"
            className="btn-upload-close"
            onClick={onClose}
            aria-label="Kapat"
          >
            ✕
          </button>
        </div>

        {/* Form Alanı */}
        <form onSubmit={handleSubmit} className="upload-doc-body">
          {/* İşyeri Bilgi Şeridi */}
          <div className="upload-workplace-strip">
            🏢 <strong>İşyeri:</strong> {workplace.title}
          </div>

          {/* 1. ADIM: Belge Türü Seçimi */}
          <div className="upload-form-group">
            <label htmlFor="docTypeSelect">
              1. Belge Türünü Seçin <span className="req-star">*</span>
            </label>
            <select
              id="docTypeSelect"
              className="upload-form-select"
              value={selectedDocCode}
              onChange={e => setSelectedDocCode(e.target.value)}
              required
            >
              {documentList.map(doc => (
                <option key={doc.code} value={doc.code}>
                  #{doc.id} - {doc.title}
                </option>
              ))}
            </select>
            <span className="upload-field-hint">
              Yüklediğiniz evrak seçilen bu belgeye işlenecek ve durumu <strong>HAZIR (Yeşil)</strong> olacaktır.
            </span>
          </div>

          {/* 2. ADIM: Belge Düzenlenme / Yapılış Tarihi */}
          <div className="upload-form-group">
            <label htmlFor="docDateInput">
              2. Belge Düzenlenme / Yapılış Tarihi <span className="req-star">*</span>
            </label>
            <input
              type="date"
              id="docDateInput"
              className="upload-form-select"
              value={documentDate}
              onChange={e => setDocumentDate(e.target.value)}
              required
            />
            <span className="upload-field-hint">
              💡 <strong>Kalan Süre Hesabı:</strong> Risk Analizi (2-4-6 yıl), Periyodik Kontroller ve Tatbikatlar için yasal süre bu tarihten itibaren hesaplanır. Yıllık planlar ise ilgili takvim yılı sonuna (01 Ocak) kadar geçerli sayılır.
            </span>
          </div>

          {/* 3. ADIM: Çoklu Dosya Yükleme Alanı (Drag & Drop + File Explorer) */}
          <div className="upload-form-group">
            <label>
              3. Bilgisayarınızdan Evrak Ekleyin (Çoklu Dosya Desteklenir) <span className="req-star">*</span>
            </label>

            <div
              className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.bmp,.tiff,.heic,.heif,image/*"
                onChange={handleFileChange}
              />
              <div className="dropzone-icon">📁</div>
              <div className="dropzone-text">
                <strong>Dosyaları buraya sürükleyip bırakın</strong> veya bilgisayarınızdan seçmek için <u>tıklayın</u>
              </div>
              <div className="dropzone-sub">
                PDF, Word, Excel, Resim vb. (Birden fazla dosya aynı anda seçilebilir)
              </div>
            </div>
          </div>

          {/* Seçilen Dosyaların Listesi */}
          {selectedFiles.length > 0 && (
            <div className="selected-files-container">
              <div className="selected-files-header">
                <span>📎 Seçilen Dosyalar ({selectedFiles.length})</span>
                <button
                  type="button"
                  className="btn-clear-all"
                  onClick={() => setSelectedFiles([])}
                >
                  Tümünü Kaldır
                </button>
              </div>
              <div className="selected-files-list">
                {selectedFiles.map((file, idx) => (
                  <div key={idx} className="file-item-chip">
                    <span className="file-item-name" title={file.name}>
                      📄 {file.name}
                    </span>
                    <span className="file-item-size">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                    <button
                      type="button"
                      className="btn-file-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(idx);
                      }}
                      title="Kaldır"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Alt Eylemler */}
          <div className="upload-doc-footer">
            <button
              type="button"
              className="btn-saas-secondary"
              onClick={onClose}
            >
              İptal
            </button>
            <button
              type="submit"
              className="btn-saas-primary pink"
              disabled={selectedFiles.length === 0}
            >
              🚀 Evrakları Kaydet ve HAZIR Yap
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadDocumentModal;
